import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { ImageViewer } from './ImageViewer';

type PreviewRequest = {
  imageUrls: string[];
  index?: number;
};

export type ImageViewerContextProps = {
  request$: BehaviorSubject<PreviewRequest | undefined>;
};

export const ImageViewerContext = createContext<ImageViewerContextProps>(
  null as any,
);

export const usePreviewImage = () => {
  const context = useContext(ImageViewerContext);
  return useCallback(
    (request: PreviewRequest) => {
      context.request$.next(request);
    },
    [context.request$],
  );
};

export const ImageViewerContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const request$ = useCreation(
      () => new BehaviorSubject<PreviewRequest | undefined>(undefined),
      [],
    );
    const [request, setRequest] = useBehaviorSubject(request$);
    const value = useMemo(() => ({ request$ }), [request$]);
    const onDismiss = useCallback(() => {
      setRequest(undefined);
    }, [setRequest]);
    return (
      <ImageViewerContext.Provider value={value}>
        <ImageViewer
          images={request?.imageUrls ?? []}
          index={request?.index ?? 0}
          show={request != null && request.imageUrls.length > 0}
          onDismiss={onDismiss}
        />
        {children}
      </ImageViewerContext.Provider>
    );
  },
);
